<template>
  <v-dialog
    :model-value="modelValue"
    class="bq-lead-dialog-root"
    content-class="bq-lead-dialog-root"
    max-width="640"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <SnackbarComp :SnackbarComponent="snackbar" />

    <v-card rounded="xl" class="user-dialog-card bq-lead-dialog">
      <div class="dialog-header bq-lead-dialog__header">
        <div class="bq-lead-dialog__header-main">
          <div class="dialog-header__icon bq-lead-dialog__icon-wrap" aria-hidden="true">
            <v-icon
              :icon="isCreate ? 'mdi-account-plus-outline' : 'mdi-account-edit-outline'"
              size="22"
            />
          </div>
          <div class="bq-lead-dialog__head-copy">
            <span class="dialog-header__title">
              {{ isCreate ? 'Add lead' : 'Edit lead' }}
            </span>
            <span class="bq-lead-dialog__subtitle">
              {{
                isCreate
                  ? 'Pick a product, then fill in its fields.'
                  : 'Refine this lead’s details.'
              }}
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn bq-lead-dialog__close"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body bq-lead-dialog__body">
        <v-form ref="formRef" v-model="isValid">
          <section class="bq-lead-dialog__section" aria-labelledby="bq-lead-context-heading">
            <h3 id="bq-lead-context-heading" class="bq-lead-dialog__section-title">
              Product
            </h3>
            <div class="bq-lead-dialog__grid bq-lead-dialog__grid--single">
              <label class="bq-lead-dialog__field">
                <span class="bq-lead-dialog__label">
                  {{ isCreate ? 'Products' : 'Product' }}
                </span>
                <v-select
                  v-model="form.context_names"
                  :items="contextOptions"
                  item-title="title"
                  item-value="value"
                  :placeholder="isCreate ? 'Select one or more products' : 'Select product'"
                  prepend-inner-icon="mdi-bookshelf"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  :multiple="isCreate"
                  :chips="isCreate"
                  :closable-chips="isCreate"
                  :rules="contextRules"
                  :loading="contextsLoading"
                  :disabled="contextsLoading && !contextOptions.length"
                  no-data-text="No products available"
                  class="bq-lead-dialog__input"
                />
                <span class="bq-lead-dialog__field-hint">
                  The fields below come from the selected product{{
                    isCreate ? 's. One lead is created per selected product' : ''
                  }}. If a matching lead already exists in a product, it is
                  updated instead of duplicated.
                </span>
              </label>
            </div>
          </section>

          <section
            v-if="selectedContextNames.length"
            class="bq-lead-dialog__section"
            aria-labelledby="bq-lead-fields-heading"
          >
            <h3 id="bq-lead-fields-heading" class="bq-lead-dialog__section-title">
              Fields
            </h3>

            <div v-if="definedFields.length" class="bq-lead-dialog__grid">
              <label
                v-for="field in definedFields"
                :key="field.name"
                class="bq-lead-dialog__field"
                :class="{ 'bq-lead-dialog__field--full': isLongTextField(field.name) }"
              >
                <span class="bq-lead-dialog__label">
                  {{ field.label }}
                  <span
                    v-if="!isRequiredField(field)"
                    class="bq-lead-dialog__label-hint"
                  >optional</span>
                </span>
                <v-select
                  v-if="field.type === 'DROPDOWN'"
                  v-model="fieldValues[field.name]"
                  :items="field.options"
                  placeholder="Select value"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  clearable
                  flat
                  :rules="rulesFor(field)"
                  class="bq-lead-dialog__input"
                />
                <v-textarea
                  v-else-if="isLongTextField(field.name)"
                  v-model="fieldValues[field.name]"
                  :placeholder="placeholderFor(field.name)"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  rows="2"
                  auto-grow
                  :rules="rulesFor(field)"
                  class="bq-lead-dialog__input bq-lead-dialog__textarea"
                />
                <div
                  v-else-if="field.type === 'PHONE_NUMBER'"
                  class="bq-lead-dialog__phone-row"
                >
                  <v-autocomplete
                    v-model="phoneDialCodes[field.name]"
                    :items="countryOptions"
                    item-title="label"
                    item-value="dial_code"
                    placeholder="+91"
                    variant="solo"
                    density="comfortable"
                    hide-details="auto"
                    flat
                    class="bq-lead-dialog__input bq-lead-dialog__phone-code"
                    @update:model-value="syncPhoneField(field.name)"
                  >
                    <template #selection="{ item }">
                      {{ item.raw.dial_code }}
                    </template>
                  </v-autocomplete>
                  <v-text-field
                    v-model="phoneNationals[field.name]"
                    placeholder="Phone number"
                    prepend-inner-icon="mdi-phone-outline"
                    variant="solo"
                    density="comfortable"
                    hide-details="auto"
                    flat
                    inputmode="tel"
                    :rules="rulesFor(field)"
                    class="bq-lead-dialog__input"
                    @update:model-value="syncPhoneField(field.name)"
                  />
                </div>
                <v-text-field
                  v-else-if="field.type === 'DATE'"
                  v-model="fieldValues[field.name]"
                  type="date"
                  prepend-inner-icon="mdi-calendar-outline"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  :rules="rulesFor(field)"
                  class="bq-lead-dialog__input"
                />
                <v-text-field
                  v-else-if="field.type === 'TIME'"
                  v-model="fieldValues[field.name]"
                  type="time"
                  prepend-inner-icon="mdi-clock-outline"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  :rules="rulesFor(field)"
                  class="bq-lead-dialog__input"
                />
                <v-text-field
                  v-else
                  v-model="fieldValues[field.name]"
                  :type="field.type === 'NUMBER' ? 'number' : field.type === 'EMAIL' ? 'email' : 'text'"
                  :placeholder="placeholderFor(field.name)"
                  :prepend-inner-icon="field.type === 'EMAIL' ? 'mdi-email-outline' : iconFor(field.name)"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  :rules="rulesFor(field)"
                  class="bq-lead-dialog__input"
                />
              </label>
            </div>
            <p v-else class="bq-lead-dialog__field-hint">
              The selected product{{ selectedContextNames.length > 1 ? 's have' : ' has' }}
              no fields registered yet.
            </p>
          </section>

          <p
            v-else-if="clearingContext"
            class="bq-lead-dialog__field-hint bq-lead-dialog__select-prompt"
          >
            This lead will be unassigned from its product. Its saved field
            values are kept.
          </p>
          <p v-else class="bq-lead-dialog__field-hint bq-lead-dialog__select-prompt">
            Select a product above to see its fields.
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer bq-lead-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="bq-lead-dialog__cancel"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="bq-lead-dialog__submit"
          :loading="saving"
          :disabled="!isValid || (!selectedContextNames.length && !clearingContext)"
          @click="submit"
        >
          {{ isCreate ? 'Add lead' : 'Save changes' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { fetchContexts } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { normalizeContextsList } from '@/utils/contexts'
import countryCodeList from '@/JsonFiles/CountryCodeList.json'
import { DEFAULT_COUNTRY_DIAL_CODE } from '@/utils/agentConfiguration'
import { detectDialCodeFromIp } from '@/utils/detectDialCode'
import {
  coerceFieldValueForApi,
  epochToDateInputValue,
  joinPhoneNumber,
  splitPhoneNumber,
  isValidEmail,
} from '@/utils/leadFieldTypes'
import '@/styles/business-queries/lead-form-dialog.css'

defineOptions({ name: 'LeadFormDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'CREATE',
    validator: (value) => ['CREATE', 'EDIT'].includes(value),
  },
  lead: { type: Object, default: null },
  defaultOrgName: { type: String, default: '' },
  saveFn: { type: Function, required: true },
  // Preselects context(s) when creating — used by the campaign detail page
  // so a lead created "inside" a campaign lands in that campaign's context
  // (auto-adoption into the campaign happens server-side on createLead).
  presetContextNames: { type: Array, default: null },
})

const emit = defineEmits(['update:modelValue', 'saved'])

// System lead attributes; everything else is a custom field. org_name is
// never sent — the server derives it from the caller's own org.
const KNOWN_LEAD_KEYS = new Set([
  'contact_name',
  'contact_email',
  'contact_number',
  'type_of_business',
  'industry',
  'message',
  'comments',
])

// contact_number is the API's one hard requirement; every other field is
// optional (the unique identifier is also required since leads match on it).
const REQUIRED_FIELD_NAMES = new Set(['contact_number'])

const LONG_TEXT_FIELDS = new Set(['message', 'comments'])

const FIELD_ICONS = {
  contact_name: 'mdi-account-outline',
  contact_email: 'mdi-email-outline',
  contact_number: 'mdi-phone-outline',
}

const FIELD_PLACEHOLDERS = {
  contact_name: 'Full name',
  contact_email: 'name@example.com',
  contact_number: 'Phone number',
  message: 'What is this lead interested in?',
  comments: 'Private notes for your team',
}

const store = useStore()
const formRef = ref(null)
const isValid = ref(false)
const saving = ref(false)
const contextsLoading = ref(false)
const contexts = ref([])
const snackbar = ref({})
const form = ref({ context_names: [] })
const fieldValues = ref({})
const phoneDialCodes = ref({})
const phoneNationals = ref({})
// Best-effort IP-detected dial code, used only as the default the dropdown
// opens with -- resolves after ensurePhoneDefaults' first (synchronous) pass
// already filled fields with DEFAULT_COUNTRY_DIAL_CODE, so a second pass
// below backfills only whatever's still untouched once it arrives.
const detectedDialCode = ref(null)

const countryOptions = computed(() =>
  (Array.isArray(countryCodeList) ? countryCodeList : []).map((country) => ({
    label: `${country.dial_code} · ${country.name}`,
    dial_code: country.dial_code,
  })),
)

function syncPhoneField(name) {
  fieldValues.value[name] = joinPhoneNumber(
    phoneDialCodes.value[name],
    phoneNationals.value[name],
  )
}

function ensurePhoneDefaults() {
  for (const field of definedFields.value) {
    if (field.type !== 'PHONE_NUMBER') continue
    if (!phoneDialCodes.value[field.name]) {
      phoneDialCodes.value[field.name] = detectedDialCode.value || DEFAULT_COUNTRY_DIAL_CODE
    }
    if (phoneNationals.value[field.name] == null) {
      phoneNationals.value[field.name] = ''
    }
  }
}

// Only in create mode, and only for fields the admin hasn't touched yet
// (still blank, still on whatever ensurePhoneDefaults filled in above) --
// never overwrite a value they've already typed or picked while this was
// still resolving.
function applyDetectedDialCodeToUntouchedFields() {
  if (!isCreate.value || !detectedDialCode.value) return
  for (const field of definedFields.value) {
    if (field.type !== 'PHONE_NUMBER') continue
    const untouched =
      !phoneNationals.value[field.name] &&
      (!phoneDialCodes.value[field.name] || phoneDialCodes.value[field.name] === DEFAULT_COUNTRY_DIAL_CODE)
    if (untouched) {
      phoneDialCodes.value[field.name] = detectedDialCode.value
    }
  }
}

const isCreate = computed(() => props.mode !== 'EDIT')

// Edit-only sentinel: choosing it sends context_name: null (clears the
// lead's context) instead of moving the lead to another context.
const NO_CONTEXT_VALUE = '__no_context__'

const selectedContextNames = computed(() => {
  const raw = form.value.context_names
  const list = Array.isArray(raw) ? raw : [raw]
  return list
    .map((name) => String(name ?? '').trim())
    .filter((name) => name && name !== NO_CONTEXT_VALUE)
})

const clearingContext = computed(
  () => !isCreate.value && form.value.context_names === NO_CONTEXT_VALUE,
)

const contextRules = [
  (v) =>
    (Array.isArray(v) ? v.length > 0 : Boolean(String(v ?? '').trim())) ||
    'Product is required',
]

const contextOptions = computed(() => {
  const options = []
  const seen = new Set()

  for (const context of contexts.value) {
    const name = String(context?.context_name ?? '').trim()
    if (!name || seen.has(name)) continue
    // Prefer active contexts; keep disabled ones labeled if already selected.
    if (context.disabled && !selectedContextNames.value.includes(name)) continue
    seen.add(name)
    options.push({
      title: context.disabled ? `${name} (disabled)` : name,
      value: name,
    })
  }

  for (const selected of selectedContextNames.value) {
    if (!seen.has(selected)) {
      options.unshift({ title: selected, value: selected })
    }
  }

  if (!isCreate.value) {
    options.push({ title: 'No product (unassigned)', value: NO_CONTEXT_VALUE })
  }

  return options
})

/** Enabled field definitions merged across all selected products. */
const definedFields = computed(() => {
  const merged = new Map()

  for (const name of selectedContextNames.value) {
    const context = contexts.value.find(
      (item) => String(item?.context_name ?? '').trim() === name,
    )
    const definitions = context?.custom_field_definitions
    if (!definitions || typeof definitions !== 'object') continue

    for (const [fieldName, definition] of Object.entries(definitions)) {
      if (definition?.enabled === false) continue
      // Internal fields (e.g. a reminder anchor date) are system-managed --
      // never offered on Add lead or Edit lead. Bulk-upload column mapping
      // reads custom_field_definitions directly, so it's still the one way
      // real values reach the field.
      if (definition?.is_internal === true) continue
      // editLead silently ignores comments — only settable via the
      // status-change flow, so don't offer it on the edit form.
      if (!isCreate.value && fieldName === 'comments') continue
      if (merged.has(fieldName)) continue
      merged.set(fieldName, {
        name: fieldName,
        type: String(definition?.type ?? 'TEXT').toUpperCase(),
        label:
          String(definition?.label ?? '').trim() || prettyFieldName(fieldName),
        options: Array.isArray(definition?.options) ? definition.options : [],
        isUniqueIdentifier: definition?.is_unique_identifier === true,
      })
    }
  }

  return [...merged.values()]
})

function prettyFieldName(name) {
  return String(name ?? '').replace(/_/g, ' ')
}

function isRequiredField(field) {
  return REQUIRED_FIELD_NAMES.has(field.name) || field.isUniqueIdentifier
}

function isLongTextField(name) {
  return LONG_TEXT_FIELDS.has(name)
}

function iconFor(name) {
  return FIELD_ICONS[name]
}

function placeholderFor(name) {
  return FIELD_PLACEHOLDERS[name] || 'Enter value'
}

function rulesFor(field) {
  const rules = []

  if (isRequiredField(field)) {
    rules.push(
      (v) =>
        Boolean(String(v ?? '').trim()) || `${field.label} is required`,
    )
  }

  if (field.name === 'contact_email' || field.type === 'EMAIL') {
    rules.push(
      (v) => !String(v ?? '').trim() || isValidEmail(v) || 'Enter a valid email',
    )
  }

  return rules
}

/** Split the collected values into known lead fields and custom_fields. */
function buildSubmitPayload() {
  const known = {}
  const custom = {}

  const definedTypes = new Map(
    definedFields.value.map((field) => [field.name, field.type]),
  )

  for (const [key, rawValue] of Object.entries(fieldValues.value)) {
    if (rawValue == null || String(rawValue).trim() === '') continue
    const type = definedTypes.get(key)
    const value = coerceFieldValueForApi(rawValue, type)
    if (value == null || value === '') continue
    if (KNOWN_LEAD_KEYS.has(key)) known[key] = value
    else custom[key] = value
  }

  return {
    context_names: selectedContextNames.value,
    ...(clearingContext.value ? { clear_context: true } : {}),
    ...known,
    custom_fields: Object.keys(custom).length ? custom : null,
  }
}

/** Prefill values from the lead being edited (known props + custom fields). */
function applyLeadValues() {
  if (props.mode !== 'EDIT' || !props.lead) return

  const clean = (value) => (value === '—' ? '' : value || '')
  const values = {
    contact_name: clean(props.lead.name),
    contact_email: clean(props.lead.email),
    // .phone first -- formatPhoneNumber()'s output, a full number with
    // country code even when the stored raw value is missing one. Prefilling
    // from phoneRaw first meant editing a lead re-saved that incomplete
    // number right back as-is instead of surfacing the corrected one.
    contact_number: props.lead.phone || props.lead.phoneRaw || '',
    type_of_business: props.lead.typeOfBusiness || '',
    industry: props.lead.industry || '',
    message: props.lead.message || '',
  }

  const existingCustom = props.lead.customFields
  if (existingCustom && typeof existingCustom === 'object') {
    for (const [key, value] of Object.entries(existingCustom)) {
      if (value == null || String(value).trim() === '') continue
      values[key] = value
    }
  }

  // Only prefill fields the context defines; the server keeps other
  // existing custom field values on merge.
  const definedByName = new Map(
    definedFields.value.map((field) => [field.name, field]),
  )
  const prefill = {}
  phoneDialCodes.value = {}
  phoneNationals.value = {}

  for (const [key, value] of Object.entries(values)) {
    if (value == null || String(value).trim() === '') continue
    const field = definedByName.get(key)
    if (!field) continue
    if (field.type === 'DATE') {
      prefill[key] = epochToDateInputValue(value)
    } else if (field.type === 'PHONE_NUMBER') {
      const parts = splitPhoneNumber(value, DEFAULT_COUNTRY_DIAL_CODE)
      phoneDialCodes.value[key] = parts.dialCode
      phoneNationals.value[key] = parts.national
      prefill[key] = joinPhoneNumber(parts.dialCode, parts.national)
    } else {
      prefill[key] = value
    }
  }

  fieldValues.value = prefill
}

function resetForm() {
  fieldValues.value = {}
  phoneDialCodes.value = {}
  phoneNationals.value = {}
  form.value = {
    context_names:
      props.mode === 'EDIT' && props.lead?.contextName
        ? props.lead.contextName
        : props.presetContextNames?.length
          ? [...props.presetContextNames]
          : [],
  }
}

async function loadContexts() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) {
    contexts.value = []
    return
  }

  contextsLoading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    if (!isApiSuccess(response)) {
      contexts.value = []
      return
    }
    contexts.value = normalizeContextsList(unwrapApiList(response))
  } catch {
    contexts.value = []
  } finally {
    contextsLoading.value = false
    applyLeadValues()
  }
}

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  const validation = await formRef.value?.validate?.()
  if (validation && validation.valid === false) return

  saving.value = true
  try {
    const result = await props.saveFn({
      mode: props.mode,
      requestId: props.lead?.requestId,
      form: buildSubmitPayload(),
    })

    if (!result?.ok) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: result?.error || 'Failed to save lead',
      }
      return
    }

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText:
        result.message ||
        (isCreate.value ? 'Lead created successfully.' : 'Lead updated successfully.'),
    }
    emit('saved', result)
    close()
  } catch (error) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(error, 'Request failed'),
    }
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    resetForm()
    void loadContexts()
    void detectDialCodeFromIp().then((dialCode) => {
      detectedDialCode.value = dialCode
      applyDetectedDialCodeToUntouchedFields()
    })
  },
)

// Switching contexts in create mode drops values for fields no selected
// context defines; edit mode re-applies the lead's values instead.
watch(
  () => selectedContextNames.value.join('|'),
  () => {
    if (props.mode === 'EDIT') {
      applyLeadValues()
      ensurePhoneDefaults()
      return
    }

    const definedNames = new Set(definedFields.value.map((field) => field.name))
    const kept = {}
    for (const [key, value] of Object.entries(fieldValues.value)) {
      if (definedNames.has(key)) kept[key] = value
    }
    fieldValues.value = kept
    ensurePhoneDefaults()
  },
)
</script>
